import { useCallback, useMemo } from 'react';
import { LinearProgress, Stack } from '@mui/material';
import { Route, Routes, useMatch } from 'react-router-dom';

import { useGetNavigationActions } from '@ocx-app/hooks/useGetNavigationActions';
import { ScreenLoaderBackdrop, SizingMultiplier } from '@ocx/ui';

import { LocationAndCategorySelector } from '@ocx/feat-local-offers';
import { OfferCard } from '../../components/OfferCard';
import { UserWelcomeSection } from './components/user-welcome-section/user-welcome-section.component';
import { useHomeController } from './hooks/useHomeController';
import { useActivateOfferButtonHint } from './hooks/useActivateOfferButtonHint';
import { HomeLayout } from './home.layout';
import { useConfiguration } from '../../modules/configuration/useConfiguration';
import { ActivateOfferButtonHint } from './components/ActivateOfferButtonHint';
import { OffersCarouselController } from './features/offers-carousel';
import { FeaturedOffersController } from './components/FeaturedOffersController';
import { RewardsController } from './components/RewardsController';
import { HowDoesItWorkCardController } from './components/HowDoesItWorkCardController';
import { BottomNavigationController } from './components/BottomNavigationController';
import { isFeaturedOffer } from '../../modules/offers/offer.type-guards';
import { OfferDetailsController } from '../OfferDetails/offer-details.controller';
import { AppRoutePath } from '../AppRoutePath';
import { useRudderStack } from '../../lib/rudderStack/useRudderStack';
import { useCreateLoyaltyEventWithStatusModal } from '../../modules/events/hooks/useCreateLoyaltyEventWithStatusModal';
import { SurveyDetailsController } from '../SurveyDetails/survey-details.controller';
import { ReferralStatusController } from './components/referral-status.controller';
import { ExternalBannerCardController } from './features/offers-carousel/external-banner-card.controller';
import { OfferCardController } from './components/OfferCardController';

export const HomeController = () => {
  const { triggerEvent } = useRudderStack();
  const surveyRouteMatch = useMatch(`${AppRoutePath.Home}${AppRoutePath.SurveyDetails}`);
  const shouldTriggerSurvey = !surveyRouteMatch;
  const { config } = useConfiguration();
  const isLocalOffersFeatureEnabled = config.localOffers.enabled;
  const {
    pointsStrategy,
    pointExpiration: { isFifo },
  } = config.loyaltyProgram;
  const { pushToTransactionHistory, pushToPointExpirationPage } = useGetNavigationActions();

  const {
    isOffersLoading,
    isMembershipLoading,
    membership,
    rewardsQuantity,
    offers,
    totalAvailableRollbackMoneyAmount,
    isTotalAvailableRollbackMoneyAmountLoading,
    refreshAll,
    isAltriaOffersUnavailableNearby,
    categories,
    selectedCategory,
    onCategoryChange,
  } = useHomeController(shouldTriggerSurvey);

  const activateOfferButtonHint = useActivateOfferButtonHint();

  const { loading: isLoyaltyEventSubmitting } = useCreateLoyaltyEventWithStatusModal({ membershipId: membership.id });

  const isHomeScreenDataLoading = isOffersLoading || isTotalAvailableRollbackMoneyAmountLoading;
  const bottom = useMemo(
    () => (
      <div>
        {isHomeScreenDataLoading && <LinearProgress sx={{ opacity: 0.8 }} />}
        <BottomNavigationController />
      </div>
    ),
    [isHomeScreenDataLoading],
  );

  const pullToRefresh = useCallback(async () => {
    triggerEvent('pull_to_refresh');
    await refreshAll();
  }, [refreshAll, triggerEvent]);

  return (
    <HomeLayout bottom={bottom} onRefresh={pullToRefresh}>
      {/* TODO move "mt" to "layout" */}
      <Stack spacing={SizingMultiplier.sm} mt={SizingMultiplier.sm}>
        <UserWelcomeSection
          loading={isMembershipLoading}
          points={membership?.points}
          name={membership?.firstName}
          pointsStrategy={pointsStrategy}
          pointExpirationEnabled={isFifo}
          handleClickToPointExpirationPage={pushToPointExpirationPage}
          handleClickToTransactionPage={pushToTransactionHistory}
        />

        <HowDoesItWorkCardController />
        <RewardsController
          rewardsQuantity={rewardsQuantity}
          totalAvailableRollbackMoneyAmount={totalAvailableRollbackMoneyAmount}
        />

        <FeaturedOffersController offers={offers.filter(isFeaturedOffer)} />

        {isLocalOffersFeatureEnabled && (
          <LocationAndCategorySelector
            isSticky
            selectedCategory={selectedCategory}
            onCategoryChange={onCategoryChange}
            categories={categories}
          />
        )}

        {isLocalOffersFeatureEnabled && (
          <Stack spacing={SizingMultiplier.sm}>
            {selectedCategory?.banners.map((banner) => (
              <ExternalBannerCardController
                key={`${banner.bannerConfigurationUuid}_${banner.trackingId}`}
                banner={banner}
                isAltriaOffersUnavailableNearby={isAltriaOffersUnavailableNearby}
              />
            ))}
            {selectedCategory?.offers.map((offer) => (
              <OfferCardController key={offer.id} offer={offer}>
                {(props) => <OfferCard {...props} />}
              </OfferCardController>
            ))}
          </Stack>
        )}

        {!isLocalOffersFeatureEnabled && (
          <OffersCarouselController
            categories={categories}
            selectedCategory={selectedCategory}
            onCategoryChange={onCategoryChange}
          />
        )}
      </Stack>
      <ActivateOfferButtonHint coords={activateOfferButtonHint.coords} isOpen={activateOfferButtonHint.isOpen} />
      <ReferralStatusController
        key={membership.id}
        id={membership.id}
        createdAt={membership.createdAt}
        incomingReferralStatus={membership.incomingReferralStatus}
      />
      <ScreenLoaderBackdrop open={isLoyaltyEventSubmitting} />
      <Routes>
        <Route path={AppRoutePath.OfferDetails} element={<OfferDetailsController returnPath={AppRoutePath.Home} />} />
        <Route path={AppRoutePath.SurveyDetails} element={<SurveyDetailsController returnPath={AppRoutePath.Home} />} />
      </Routes>
    </HomeLayout>
  );
};
